// =============================================
// autoGenerateBulkRunner.ts — sekwencyjna petla po wielu mapach
// =============================================
// STOP-E planu auto-generate-maps. Wraps runMapAutoGenerate dla N map naraz.
//
// KRYTYCZNE (KOREKTA 2): ZERO concurrency >1 na poziomie map. Jedna mapa naraz.
// Wewnatrz mapy zachowany istniejacy parallelism z DB pipeline_config (np.
// runBatchNodeGeneration uzywa parallel_batches=3 z config — to NIE rusza).
//
// Outer runLock("map_content") — chroni przed kolizja ze standalone bulk
// (ten sam mechanizm co Phase Content Engine).
//
// Pre-run: cleanup_stale_map_locks RPC (KOREKTA 6.3) — zwalnia mapy ktore
// nie zostaly zwolnione przez crashed runner (>60 min temu claimed).
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { tryAcquire, release } from "@/services/llm/runLock";
import {
  runMapAutoGenerate,
  type TargetStage,
  type ProgressEvent,
  type RunMapResult,
} from "@/services/autoGenerate/autoGenerateMapRunner";
import { logPublic, logError } from "@/services/contentEngine/contentLogger";

export interface BulkAutoGenerateOptions {
  targetStage: TargetStage;
  skipDone: boolean;
  withSerpValidation: boolean;
}

export interface BulkControl {
  pauseRef: { current: boolean };
  stopRef: { current: boolean };
}

export interface BulkMapOutcome {
  mapId: string;
  success: boolean;
  completedStages: string[];
  errorStage?: string;
  error?: string;
  costUsd: number;
  alreadyLocked?: boolean;
  durationMs: number;
}

export interface BulkProgressEvent {
  type:
    | "bulk_start"
    | "cleanup_done"
    | "map_start"
    | "map_stage"
    | "map_done"
    | "paused"
    | "stopping"
    | "bulk_done";
  /** Currently processing map (gdy map_*). */
  currentMapId?: string;
  /** Per-stage event z runMapAutoGenerate. */
  stageEvent?: ProgressEvent;
  /** Statistics. */
  done?: number;
  total?: number;
  failed?: number;
  cleanedLocks?: number;
  /** Final summary (na bulk_done). */
  outcomes?: BulkMapOutcome[];
}

export interface BulkSummary {
  total: number;
  ok: BulkMapOutcome[];
  failed: BulkMapOutcome[];
  skipped: string[];
  stopped: boolean;
  totalCostUsd: number;
  durationMs: number;
}

/**
 * Wywoluje runMapAutoGenerate sekwencyjnie dla mapIds. Zwraca pelne summary.
 *
 * Outer lock: tryAcquire("map_content") raz na partie. Standalone bulk leci
 * rownolegle bedzie blocked (i odwrotnie — jak standalone leci, nasz bulk
 * dostanie blockedBy='standalone' i nie wystartuje).
 *
 * onProgress fires kazde przejscie stage'u + per-mapa start/done. UI moze
 * subscribe by aktualizowac overlay.
 */
export async function runBulkAutoGenerate(
  mapIds: string[],
  userId: string,
  options: BulkAutoGenerateOptions,
  control: BulkControl,
  onProgress: (event: BulkProgressEvent) => void,
): Promise<BulkSummary> {
  const startTime = Date.now();
  const outcomes: BulkMapOutcome[] = [];
  const ok: BulkMapOutcome[] = [];
  const failed: BulkMapOutcome[] = [];
  const skipped: string[] = [];
  let totalCost = 0;
  let stopped = false;

  // ============ Outer runLock acquire ============
  const lockResult = tryAcquire("map_content");
  if (!lockResult.ok) {
    logError(
      "AUTO_GEN",
      `Bulk auto-generate ZABLOKOWANY przez ${lockResult.blockedBy} (runLock zajety)`,
    );
    return {
      total: mapIds.length,
      ok: [],
      failed: [],
      skipped: mapIds,
      stopped: false,
      totalCostUsd: 0,
      durationMs: Date.now() - startTime,
    };
  }

  try {
    // ============ Pre-run: cleanup stale locks ============
    let cleanedLocks = 0;
    try {
      const { data } = await (supabase as any).rpc("cleanup_stale_map_locks");
      cleanedLocks = (data || []).length;
      if (cleanedLocks > 0) {
        logPublic(
          "AUTO_GEN",
          `Cleanup stale locks: zwolniono ${cleanedLocks} map (locked > 60 min)`,
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "unknown";
      logError("AUTO_GEN", `cleanup_stale_map_locks RPC error: ${msg}`);
      // NIE blokujace — kontynuujemy.
    }

    onProgress({
      type: "bulk_start",
      total: mapIds.length,
    });
    onProgress({
      type: "cleanup_done",
      cleanedLocks,
    });

    logPublic(
      "AUTO_GEN",
      `Bulk START: ${mapIds.length} map, target=${options.targetStage}, skipDone=${options.skipDone}, serp=${options.withSerpValidation}`,
    );

    // ============ Sekwencyjna petla po mapach ============
    for (let i = 0; i < mapIds.length; i++) {
      const mapId = mapIds[i];

      // Stop?
      if (control.stopRef.current) {
        stopped = true;
        // Reszta -> skipped
        for (let j = i; j < mapIds.length; j++) skipped.push(mapIds[j]);
        logPublic(
          "AUTO_GEN",
          `Bulk STOPPED: ${i}/${mapIds.length} przetworzonych, ${skipped.length} skipped`,
        );
        onProgress({
          type: "stopping",
          done: i,
          total: mapIds.length,
        });
        break;
      }

      // Pause?
      while (control.pauseRef.current && !control.stopRef.current) {
        onProgress({
          type: "paused",
          done: i,
          total: mapIds.length,
        });
        await new Promise((r) => setTimeout(r, 1000));
      }

      // Stop podczas pauzy?
      if (control.stopRef.current) {
        stopped = true;
        for (let j = i; j < mapIds.length; j++) skipped.push(mapIds[j]);
        break;
      }

      // ============ Run map ============
      onProgress({
        type: "map_start",
        currentMapId: mapId,
        done: i,
        total: mapIds.length,
      });

      const mapStart = Date.now();
      const result: RunMapResult = await runMapAutoGenerate({
        mapId,
        userId,
        targetStage: options.targetStage,
        skipDone: options.skipDone,
        withSerpValidation: options.withSerpValidation,
        isPausedRef: control.pauseRef,
        isStopRequestedRef: control.stopRef,
        onProgress: (stageEvent) => {
          onProgress({
            type: "map_stage",
            currentMapId: mapId,
            stageEvent,
            done: i,
            total: mapIds.length,
          });
        },
      });

      const outcome: BulkMapOutcome = {
        mapId,
        success: result.success,
        completedStages: result.completedStages,
        errorStage: result.errorStage,
        error: result.error,
        costUsd: result.costUsd,
        alreadyLocked: result.alreadyLocked,
        durationMs: Date.now() - mapStart,
      };
      outcomes.push(outcome);
      totalCost += result.costUsd;

      if (result.success) {
        ok.push(outcome);
      } else {
        failed.push(outcome);
        logError(
          "AUTO_GEN_ERR",
          `Bulk: mapa ${mapId} FAILED (stage=${result.errorStage}, err=${result.error}, cost=$${result.costUsd.toFixed(4)})`,
        );
      }

      onProgress({
        type: "map_done",
        currentMapId: mapId,
        done: i + 1,
        total: mapIds.length,
        failed: failed.length,
      });
      // KOREKTA 3: NIE retry. Continue do nastepnej mapy.
    }

    // ============ Final summary ============
    const summary: BulkSummary = {
      total: mapIds.length,
      ok,
      failed,
      skipped,
      stopped,
      totalCostUsd: totalCost,
      durationMs: Date.now() - startTime,
    };

    logPublic(
      "AUTO_GEN",
      `Bulk DONE: ${ok.length} OK, ${failed.length} FAILED, ${skipped.length} SKIPPED, total cost $${totalCost.toFixed(4)}, ${(summary.durationMs / 1000).toFixed(1)}s`,
    );
    onProgress({
      type: "bulk_done",
      done: ok.length,
      total: mapIds.length,
      failed: failed.length,
      outcomes,
    });

    return summary;
  } finally {
    // ============ Release outer lock ============
    release("map_content");
  }
}
